import { forwardRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { FadeUpProps } from '@/types/components/onboarding'

/**
 * FadeUp — shared entrance animation wrapper for all onboarding steps.
 *
 * Matches Claude's onboarding animation pattern:
 *   - opacity: 0 → 1
 *   - translateY: 24px → 0
 *   - duration: 500ms (capped per skill guidelines)
 *   - easing: cubic-bezier(0.22, 1, 0.36, 1) — expo ease-out
 *
 * Usage — stagger by incrementing `delay` per element:
 *
 *   <FadeUp delay={0}>  <Heading>Title</Heading>    </FadeUp>
 *   <FadeUp delay={120}><Text>Subtext</Text>         </FadeUp>
 *   <FadeUp delay={200}><Input ... />                </FadeUp>
 *   <FadeUp delay={300}><Button>Continue</Button>    </FadeUp>
 *
 * Respects `prefers-reduced-motion`: when the OS flag is set, renders
 * children directly in a plain div with no animation at all.
 */
export const FadeUp = forwardRef<HTMLDivElement, FadeUpProps>(
  function FadeUp({ delay = 0, children, className }, ref) {
    const shouldReduceMotion = useReducedMotion()

    if (shouldReduceMotion) {
      return (
        <div ref={ref} className={cn(className)}>
          {children}
        </div>
      )
    }

    return (
      <motion.div
        ref={ref}
        className={cn(className)}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          delay: delay / 1000,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {children}
      </motion.div>
    )
  },
)
